import { Link, Outlet } from '@tanstack/react-router'

import { ReminderDetail } from '@/screens/ReminderDetail'
import { RemindersTab } from '@/screens/RemindersTab'
import { WhatsAppMessagesTab } from '@/screens/WhatsAppMessagesTab'
import { WhatsAppSettingsTab } from '@/screens/WhatsAppSettingsTab'

export type RemindersPageTab =
  | 'scheduled'
  | 'archived'
  | 'messages'
  | 'configure'
  | 'reminder'

const tabs = [
  { to: '/reminders', label: 'Scheduled', exact: true },
  { to: '/reminders/messages', label: 'Messages', exact: false },
  { to: '/reminders/archived', label: 'Archived', exact: false },
  { to: '/reminders/configure', label: 'Configure', exact: false },
] as const

/**
 * The Reminders page: what is scheduled for customers waiting to collect, what
 * has ended, the WhatsApp messages those reminders queued, and the
 * configuration. Each section is its own URL (/reminders, /reminders/archived,
 * /reminders/messages, /reminders/configure) and one reminder is
 * /reminders/<id>, so every view is linkable and keeps its own table filters.
 */
export function RemindersPage({
  tab,
  reminderId,
}: {
  tab: RemindersPageTab
  reminderId?: string
}) {
  return (
    <div className="bg-slate-50 text-slate-900">
      <main className="max-w-container mx-auto px-4 py-6 space-y-5">
        <header className="space-y-3">
          <h1 className="text-xl font-semibold">Reminders</h1>
          <nav
            aria-label="Reminders sections"
            className="flex gap-1 border-b border-slate-200"
          >
            {tabs.map(({ to, label, exact }) => (
              <Link
                key={to}
                to={to}
                activeOptions={{ exact, includeSearch: false }}
                className="-mb-px border-b-2 border-transparent px-3 py-2 text-[13px] font-medium text-slate-500 hover:text-slate-700"
                activeProps={{
                  className:
                    '-mb-px border-b-2 border-slate-900 px-3 py-2 text-[13px] font-medium text-slate-900',
                  'aria-current': 'page',
                }}
              >
                {label}
              </Link>
            ))}
          </nav>
        </header>

        {tab === 'scheduled' ? <RemindersTab scope="active" /> : null}
        {tab === 'archived' ? <RemindersTab scope="archived" /> : null}
        {tab === 'messages' ? <WhatsAppMessagesTab /> : null}
        {tab === 'configure' ? <WhatsAppSettingsTab /> : null}
        {tab === 'reminder' && reminderId ? <ReminderDetail reminderId={reminderId} /> : null}
        <Outlet />
      </main>
    </div>
  )
}
